/**
 * 학원 정보 입력 폼 (공용) — 등록(ConsoleRegister)·정보수정(ConsoleSettingsPage) 공유.
 * 표현 전용: 상태(form)와 핸들러는 부모가 주입.
 */
import React, { useState } from 'react';
import styled from 'styled-components';
import { IoCameraOutline, IoAddCircleOutline, IoClose, IoAdd, IoSearch, IoPlay, IoLogoYoutube } from 'react-icons/io5';
import { GhostBtn } from './consoleUi';

export const FIELD_OPTS = [
  { v: 'study', l: '교과 과정' },
  { v: 'sports', l: '운동 / 태권도' },
  { v: 'arts', l: '음악 / 미술' },
];
export const TYPE_OPTS = [
  { v: 'academy', l: '학원' },
  { v: 'home', l: '교습소 · 공부방' },
  { v: 'personal', l: '개인레슨' },
];

export const linesToArr = (s) => s.split('\n').map((x) => x.trim()).filter(Boolean);
export const arrToLines = (a) => (Array.isArray(a) ? a.join('\n') : '');

/** 유튜브 URL/ID → 11자리 videoId 추출 (실패 시 '') */
export const parseYouTubeId = (url) => {
  if (!url) return '';
  const s = String(url).trim();
  const m = s.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/|v\/))([\w-]{11})/);
  if (m) return m[1];
  if (/^[\w-]{11}$/.test(s)) return s;   // ID만 붙여넣은 경우
  return '';
};
/** videoId → 썸네일 이미지 URL */
export const ytThumb = (id) => (id ? `https://img.youtube.com/vi/${id}/mqdefault.jpg` : '');

/** 빈 폼 초기값 */
export const emptyForm = {
  field: '', type: '', name: '', grades: '', location: '', addressDetail: '', phone: '',
  hours: '', shuttle: null, intro: '',
  photos: [], videos: [],
  hashtags: '', regNo: '', homepage: '', instagram: '', blog: '',
  tuition: [],
};

/** Firestore academy/draft → 폼 상태 */
export const formFromAcademy = (a) => {
  const promo = (a.promoPhotos || []).map((p) => (typeof p === 'string' ? { url: p, label: '' } : p));
  const photos = a.photoUrl ? [{ url: a.photoUrl, label: '' }, ...promo] : promo;
  return {
    field: a.field || '', type: a.type || '', name: a.name || '',
    grades: arrToLines(a.grades), location: a.location || '', addressDetail: a.addressDetail || '', phone: a.phone || '',
    hours: arrToLines(a.hours),
    shuttle: typeof a.shuttle === 'boolean' ? a.shuttle : null,
    intro: a.intro || '',
    photos,
    videos: (a.videos || []).map((v) => {
      const o = typeof v === 'string' ? { title: v } : { ...v };
      if (!o.videoId && o.url) o.videoId = parseYouTubeId(o.url);
      return o;
    }),
    hashtags: (a.hashtags || []).join(', '), regNo: a.regNo || '',
    homepage: a.homepage || '', instagram: a.instagram || '', blog: a.blog || '',
    tuition: a.tuition || [],
  };
};

/** 폼 상태 → Firestore 저장 데이터 (첫 사진=대표) */
export const buildAcademyData = (form) => ({
  field: form.field, type: form.type, name: form.name.trim(),
  grades: linesToArr(form.grades), location: form.location.trim(), addressDetail: form.addressDetail.trim(), phone: form.phone.trim(),
  hours: linesToArr(form.hours), shuttle: !!form.shuttle, intro: form.intro.trim(),
  photoUrl: form.photos[0]?.url || '',
  promoPhotos: form.photos.slice(1),
  videos: form.videos,
  hashtags: form.hashtags.split(',').map((x) => x.trim().replace(/^#/, '')).filter(Boolean),
  regNo: form.regNo.trim(), homepage: form.homepage.trim(),
  instagram: form.instagram.trim(), blog: form.blog.trim(),
  tuition: form.tuition.filter((t) => t.label?.trim() || t.price?.trim()),
});

/** 필수 미입력 항목 목록 */
export const missingRequired = (form) => {
  const m = [];
  if (!form.field) m.push('분야');
  if (!form.type) m.push('운영 형태');
  if (!form.name.trim()) m.push('학원 이름');
  if (linesToArr(form.grades).length === 0) m.push('수업 대상');
  if (!form.location.trim()) m.push('주소');
  if (!form.phone.trim()) m.push('연락처');
  if (linesToArr(form.hours).length === 0) m.push('영업시간');
  if (form.shuttle === null) m.push('셔틀버스');
  if (!form.intro.trim()) m.push('학원 소개');
  if (form.photos.length === 0) m.push('사진');
  return m;
};

const AcademyForm = ({
  form, set, uploading, photoInputId = 'academy-photos',
  onAddPhotos, removePhoto, setPhotoLabel,
  addTuition, setTuition, delTuition, openPostcode,
}) => {
  const [vTitle, setVTitle] = useState('');
  const [vUrl, setVUrl] = useState('');
  // 사진 드래그 정렬
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const reorderPhotos = (from, to) => {
    if (from == null || to == null || from === to) return;
    const arr = [...form.photos];
    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);
    set('photos', arr);
  };
  const onPhotoDrop = (to) => {
    reorderPhotos(dragIdx, to);
    setDragIdx(null); setOverIdx(null);
  };
  const addVideo = () => {
    const title = vTitle.trim();
    const url = vUrl.trim();
    const videoId = parseYouTubeId(url);
    if (!title) { alert('영상 제목을 입력해 주세요.'); return; }
    if (!videoId) { alert('유튜브 주소가 올바르지 않아요.\n예) https://youtu.be/xxxxxxxxxxx'); return; }
    if (form.videos.some((x) => x.videoId === videoId)) { setVTitle(''); setVUrl(''); return; }
    set('videos', [...form.videos, { title, url, videoId }]);
    setVTitle(''); setVUrl('');
  };
  const removeVideo = (i) => set('videos', form.videos.filter((_, idx) => idx !== i));
  return (
  <>
    {/* 사진 (다중) — 첫 장이 대표사진 */}
    <SecLab>사진 <Req>*</Req> <Hint>여러 장의 사진을 올려 학원을 홍보하세요 (첫 사진이 대표)</Hint></SecLab>
    <PhotoGrid>
      {form.photos.map((p, i) => (
        <PhotoCell
          key={i}
          $over={overIdx === i && dragIdx !== i}
          onDragOver={(e) => { e.preventDefault(); if (overIdx !== i) setOverIdx(i); }}
          onDrop={() => onPhotoDrop(i)}
        >
          <Thumb
            $url={p.url}
            $dragging={dragIdx === i}
            draggable
            onDragStart={(e) => {
              // X 버튼에서 시작한 드래그는 취소(삭제 클릭 보존)
              if (e.target.closest('button')) { e.preventDefault(); return; }
              setDragIdx(i);
            }}
            onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
          >
            {i === 0 && <RepBadge>대표</RepBadge>}
            <RemovePhoto
              type="button"
              draggable={false}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); removePhoto(i); }}
            >
              <IoClose size={15} />
            </RemovePhoto>
          </Thumb>
          <CapInput placeholder="사진 설명 (선택)" value={p.label} onChange={(e) => setPhotoLabel(i, e.target.value)} />
        </PhotoCell>
      ))}
      <AddPhoto as="label" htmlFor={photoInputId}>
        <IoCameraOutline size={24} />
        <span>{uploading ? '업로드 중…' : '사진 추가'}</span>
        <input id={photoInputId} type="file" accept="image/*" multiple hidden onChange={onAddPhotos} disabled={uploading} />
      </AddPhoto>
    </PhotoGrid>

    <Grid>
      <FieldBox>
        <Lab>분야 <Req>*</Req></Lab>
        <Segmented>
          {FIELD_OPTS.map((o) => <Seg key={o.v} $on={form.field === o.v} onClick={() => set('field', o.v)}>{o.l}</Seg>)}
        </Segmented>
      </FieldBox>
      <FieldBox>
        <Lab>운영 형태 <Req>*</Req></Lab>
        <Segmented>
          {TYPE_OPTS.map((o) => <Seg key={o.v} $on={form.type === o.v} onClick={() => set('type', o.v)}>{o.l}</Seg>)}
        </Segmented>
      </FieldBox>

      <FieldBox>
        <Lab>학원 이름 <Req>*</Req></Lab>
        <Input value={form.name} onChange={(e) => set('name', e.target.value)} />
      </FieldBox>
      <FieldBox>
        <Lab>연락처 <Req>*</Req></Lab>
        <Input value={form.phone} onChange={(e) => set('phone', e.target.value)} />
      </FieldBox>

      <FieldBox $full>
        <Lab>주소 <Req>*</Req></Lab>
        <AddrRow>
          <Input readOnly value={form.location} placeholder="주소 검색을 눌러 입력" onClick={openPostcode} style={{ cursor: 'pointer', background: '#FBFCFE' }} />
          <GhostBtn type="button" onClick={openPostcode}><IoSearch size={16} /> 주소 검색</GhostBtn>
        </AddrRow>
        <Input
          value={form.addressDetail}
          onChange={(e) => set('addressDetail', e.target.value)}
          placeholder="상세주소 (동·층·호수 등)"
          style={{ marginTop: 8 }}
        />
      </FieldBox>

      <FieldBox>
        <Lab>수업 대상 <Req>*</Req> <Hint>한 줄에 하나씩</Hint></Lab>
        <Area value={form.grades} onChange={(e) => set('grades', e.target.value)} placeholder={'초등 저학년\n초등 고학년\n중등'} />
      </FieldBox>
      <FieldBox>
        <Lab>영업시간 <Req>*</Req> <Hint>한 줄에 하나씩</Hint></Lab>
        <Area value={form.hours} onChange={(e) => set('hours', e.target.value)} placeholder={'평일 14:00~22:00\n토 10:00~18:00'} />
      </FieldBox>

      <FieldBox>
        <Lab>셔틀버스 <Req>*</Req></Lab>
        <Segmented>
          <Seg $on={form.shuttle === true} onClick={() => set('shuttle', true)}>운행해요</Seg>
          <Seg $on={form.shuttle === false} onClick={() => set('shuttle', false)}>운행 안 해요</Seg>
        </Segmented>
      </FieldBox>
      <FieldBox $full>
        <Lab>학원 소개 <Req>*</Req></Lab>
        <Area $tall value={form.intro} onChange={(e) => set('intro', e.target.value)} />
      </FieldBox>

      {/* 수업영상 (유튜브) */}
      <FieldBox $full>
        <Lab>수업영상 <Hint>유튜브 영상만 등록할 수 있어요 · 선택</Hint></Lab>
        <VideoInputBox>
          <MiniLab>① 영상 제목</MiniLab>
          <Input
            value={vTitle}
            placeholder="예: 유리드믹스 수업 미리보기"
            onChange={(e) => setVTitle(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addVideo(); } }}
          />
          <MiniLab>② 유튜브 주소</MiniLab>
          <VideoInputRow>
            <YtInputWrap>
              <IoLogoYoutube size={18} color="#FF0000" />
              <YtInput
                value={vUrl}
                placeholder="https://youtu.be/… 붙여넣기"
                onChange={(e) => setVUrl(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addVideo(); } }}
              />
            </YtInputWrap>
            <GhostBtn type="button" onClick={addVideo} style={{ whiteSpace: 'nowrap', flexShrink: 0 }}><IoAdd size={18} /> 추가</GhostBtn>
          </VideoInputRow>
        </VideoInputBox>
        {form.videos.length > 0 && (
          <VideoList>
            {form.videos.map((v, i) => {
              const vid = v.videoId || parseYouTubeId(v.url);
              return (
                <VideoRow key={i}>
                  {vid
                    ? <VideoThumb $url={ytThumb(vid)}><IoPlay size={16} /></VideoThumb>
                    : <VideoThumbEmpty><IoLogoYoutube size={20} color="#9CA3AF" /></VideoThumbEmpty>}
                  <VideoMeta>
                    <VideoTitleTxt>{v.title || '제목 없음'}</VideoTitleTxt>
                    <VideoUrl>{v.url || ''}</VideoUrl>
                  </VideoMeta>
                  <DelBtn type="button" onClick={() => removeVideo(i)}><IoClose size={16} /></DelBtn>
                </VideoRow>
              );
            })}
          </VideoList>
        )}
      </FieldBox>

      <FieldBox $full>
        <Lab>학원비 <Hint>선택</Hint></Lab>
        {form.tuition.map((t, i) => (
          <TuiRow key={i}>
            <Input style={{ flex: 1 }} placeholder="항목 (예: 정규반)" value={t.label} onChange={(e) => setTuition(i, 'label', e.target.value)} />
            <Input style={{ width: 160 }} placeholder="금액 (예: 200,000)" value={t.price} onChange={(e) => setTuition(i, 'price', e.target.value)} />
            <DelBtn onClick={() => delTuition(i)}><IoClose size={18} /></DelBtn>
          </TuiRow>
        ))}
        <AddTui type="button" onClick={addTuition}><IoAddCircleOutline size={18} /> 학원비 항목 추가</AddTui>
      </FieldBox>

      <FieldBox>
        <Lab>해시태그 <Hint>쉼표로 구분</Hint></Lab>
        <Input value={form.hashtags} onChange={(e) => set('hashtags', e.target.value)} placeholder="소수정예, 내신대비" />
      </FieldBox>
      <FieldBox>
        <Lab>홈페이지 <Hint>선택</Hint></Lab>
        <Input value={form.homepage} onChange={(e) => set('homepage', e.target.value)} />
      </FieldBox>
      <FieldBox>
        <Lab>인스타그램 <Hint>선택</Hint></Lab>
        <Input value={form.instagram} onChange={(e) => set('instagram', e.target.value)} />
      </FieldBox>
      <FieldBox>
        <Lab>블로그 <Hint>선택</Hint></Lab>
        <Input value={form.blog} onChange={(e) => set('blog', e.target.value)} />
      </FieldBox>
    </Grid>
  </>
  );
};

export default AcademyForm;

const SecLab = styled.div` font-size: 13px; font-weight: 700; color: #374151; margin-bottom: 12px; `;
const PhotoGrid = styled.div` display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 24px; `;
const PhotoCell = styled.div`
  width: 120px;
  ${({ $over }) => $over && `& > div:first-child { box-shadow: 0 0 0 2px #4A7CFE; }`}
`;
const Thumb = styled.div`
  position: relative; width: 120px; height: 120px; border-radius: 12px;
  background: ${({ $url }) => `#eee url(${$url}) center/cover`}; border: 1px solid #EAEDF3;
  cursor: grab;
  opacity: ${({ $dragging }) => ($dragging ? 0.4 : 1)};
  &:active { cursor: grabbing; }
`;
const RepBadge = styled.span`
  position: absolute; left: 6px; top: 6px; padding: 2px 8px; border-radius: 9px;
  background: #4A7CFE; color: #fff; font-size: 11px; font-weight: 800;
`;
const RemovePhoto = styled.button`
  position: absolute; right: 5px; top: 5px; width: 22px; height: 22px; border-radius: 50%;
  background: rgba(17,24,39,0.7); color: #fff; display: flex; align-items: center; justify-content: center;
`;
const CapInput = styled.input`
  width: 120px; margin-top: 6px; padding: 6px 8px; border: 1px solid #E2E5EA; border-radius: 7px;
  font-size: 12px; color: #374151;
  &:focus { border-color: #4A7CFE; outline: none; }
  &::placeholder { color: #C4C9D2; }
`;
const AddPhoto = styled.div`
  width: 120px; height: 120px; border-radius: 12px; border: 1.5px dashed #CBD2DC;
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px;
  color: #9CA3AF; cursor: pointer;
  span { font-size: 12px; font-weight: 600; }
  &:hover { border-color: #4A7CFE; color: #4A7CFE; background: #FBFCFE; }
`;

const Grid = styled.div` display: grid; grid-template-columns: 1fr 1fr; gap: 18px 20px; align-items: start; `;
const FieldBox = styled.div` grid-column: ${({ $full }) => ($full ? '1 / -1' : 'auto')}; `;
const Lab = styled.div` font-size: 13px; font-weight: 700; color: #374151; margin-bottom: 8px; `;
const Req = styled.span` color: #DC2626; font-weight: 800; `;
const Hint = styled.span` font-size: 12px; font-weight: 500; color: #9CA3AF; margin-left: 4px; `;
const Input = styled.input`
  width: 100%; padding: 11px 13px; border: 1px solid #E2E5EA; border-radius: 9px; font-size: 14px; color: #111827;
  &:focus { border-color: #4A7CFE; outline: none; }
  &::placeholder { color: #C4C9D2; }
`;
const Area = styled.textarea`
  width: 100%; padding: 11px 13px; border: 1px solid #E2E5EA; border-radius: 9px; font-size: 14px; color: #111827;
  resize: vertical; min-height: ${({ $tall }) => ($tall ? '180px' : '76px')};
  &:focus { border-color: #4A7CFE; outline: none; }
  &::placeholder { color: #C4C9D2; }
`;
const AddrRow = styled.div` display: flex; gap: 8px; align-items: center; `;
const Segmented = styled.div` display: flex; gap: 8px; flex-wrap: wrap; `;
const Seg = styled.button`
  padding: 10px 16px; border-radius: 9px; font-size: 14px; font-weight: 700;
  background: ${({ $on }) => ($on ? '#EEF2FF' : '#F3F4F6')};
  color: ${({ $on }) => ($on ? '#1D4ED8' : '#6B7280')};
  border: 1px solid ${({ $on }) => ($on ? '#C7D2FE' : 'transparent')};
`;
const VideoInputBox = styled.div`
  display: flex; flex-direction: column; gap: 6px;
  padding: 12px; border: 1px solid #EAEDF3; border-radius: 10px; background: #FBFCFE;
`;
const MiniLab = styled.span` font-size: 12px; font-weight: 700; color: #6B7280; margin-top: 2px; `;
const VideoInputRow = styled.div` display: flex; gap: 8px; align-items: center; flex-wrap: wrap; `;
const YtInputWrap = styled.div`
  flex: 1; min-width: 220px; display: flex; align-items: center; gap: 8px;
  padding: 0 13px; border: 1px solid #E2E5EA; border-radius: 9px; background: #fff;
  &:focus-within { border-color: #4A7CFE; }
  svg { flex-shrink: 0; }
`;
const YtInput = styled.input`
  flex: 1; min-width: 0; padding: 11px 0; border: none; outline: none;
  font-size: 14px; color: #111827; background: transparent;
  &::placeholder { color: #C4C9D2; }
`;
const VideoList = styled.div` display: flex; flex-direction: column; gap: 8px; margin-top: 10px; `;
const VideoRow = styled.div` display: flex; align-items: center; gap: 10px; padding: 8px 10px; background: #F7F8FA; border-radius: 10px; `;
const VideoThumb = styled.div`
  flex-shrink: 0; width: 64px; height: 40px; border-radius: 6px;
  background: ${({ $url }) => `#000 url(${$url}) center/cover`};
  display: flex; align-items: center; justify-content: center; color: #fff;
  svg { filter: drop-shadow(0 1px 2px rgba(0,0,0,0.6)); }
`;
const VideoThumbEmpty = styled.div`
  flex-shrink: 0; width: 64px; height: 40px; border-radius: 6px;
  background: #F1F3F6; display: flex; align-items: center; justify-content: center;
`;
const VideoMeta = styled.div` flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; `;
const VideoTitleTxt = styled.span` font-size: 13.5px; font-weight: 700; color: #1F2937; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; `;
const VideoUrl = styled.span` font-size: 12px; color: #9CA3AF; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; `;
const TuiRow = styled.div` display: flex; gap: 8px; align-items: center; margin-bottom: 8px; `;
const DelBtn = styled.button` padding: 8px; border-radius: 7px; color: #9CA3AF; &:hover { background: #F3F4F6; color: #EF4444; } `;
const AddTui = styled.button` display: inline-flex; align-items: center; gap: 6px; font-size: 13px; font-weight: 700; color: #4A7CFE; padding: 6px 2px; `;
